import { useEffect, useState } from "react";
import { Save, Loader2, Layout, Search, UserPlus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

const Block = ({ title, children }) => (
  <section className="bg-white border border-slate-200 rounded-md p-6">
    <h3 className="font-heading text-lg font-medium text-slate-900 mb-4">{title}</h3>
    <div className="space-y-4">{children}</div>
  </section>
);

export default function AdminSettings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [brand, setBrand] = useState({ site_name: "", site_tagline: "" });
  const [savingBrand, setSavingBrand] = useState(false);
  const [seo, setSeo] = useState({ page_title: "", meta_description: "", meta_keywords: "" });
  const [savingSeo, setSavingSeo] = useState(false);
  const [signupEnabled, setSignupEnabled] = useState(false);
  const [savingAccess, setSavingAccess] = useState(false);

  useEffect(() => {
    api
      .get("/settings/landing")
      .then((res) => setData(res.data))
      .catch(() => toast.error("Failed to load settings"))
      .finally(() => setLoading(false));
    api
      .get("/settings/brand")
      .then((res) => setBrand(res.data))
      .catch(() => {});
    api
      .get("/settings/seo")
      .then((res) => setSeo(res.data))
      .catch(() => {});
    api
      .get("/settings/access")
      .then((res) => setSignupEnabled(!!res.data.signup_enabled))
      .catch(() => {});
  }, []);

  const saveAccess = async (value) => {
    setSignupEnabled(value);
    setSavingAccess(true);
    try {
      await api.put("/admin/settings/access", { signup_enabled: value });
      toast.success(value ? "New signups are now open" : "New signups are now closed (demo mode)");
    } catch (err) {
      setSignupEnabled(!value);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingAccess(false);
    }
  };

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setFeature = (i, k, v) =>
    setData((d) => {
      const features = d.features.map((f, idx) => (idx === i ? { ...f, [k]: v } : f));
      return { ...d, features };
    });

  const saveBrand = async () => {
    setSavingBrand(true);
    try {
      await api.put("/admin/settings/brand", brand);
      toast.success("Site name updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingBrand(false);
    }
  };

  const saveSeo = async () => {
    setSavingSeo(true);
    try {
      await api.put("/admin/settings/seo", seo);
      if (seo.page_title) document.title = seo.page_title;
      toast.success("SEO settings updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingSeo(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings/landing", data);
      toast.success("Front page updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data)
    return (
      <div className="flex items-center gap-2 text-slate-500 py-16 justify-center">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading…
      </div>
    );

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div>
          <h2 className="font-heading text-2xl tracking-tight font-medium text-slate-900 flex items-center gap-2">
            <Layout className="w-6 h-6 text-[#1E3A8A]" /> Front Page Content
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Edit the public landing page merchants see before signing in.
          </p>
        </div>
        <Button
          onClick={save}
          disabled={saving}
          className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white"
          data-testid="save-settings-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save changes
        </Button>
      </div>

      <div className="grid gap-6 max-w-3xl">
        <section className="bg-white border border-slate-200 rounded-md p-6" data-testid="access-block">
          <h3 className="font-heading text-lg font-medium text-slate-900 mb-1 flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-[#1E3A8A]" /> New Merchant Signups
          </h3>
          <p className="text-sm text-slate-500 mb-4">
            Turn this off while the site is for demonstration only. New merchants won't be able to
            register (existing accounts can still sign in).
          </p>
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-md">
            <div>
              <p className="text-sm font-medium text-slate-800">Allow new merchant signups</p>
              <p className="text-xs text-slate-500 mt-0.5">
                {signupEnabled ? "Signups are OPEN — anyone can create an account." : "Signups are CLOSED — demonstration mode."}
              </p>
            </div>
            <Switch
              checked={signupEnabled}
              disabled={savingAccess}
              onCheckedChange={saveAccess}
              data-testid="toggle-signup-enabled"
            />
          </div>
        </section>

        <Block title="Site Branding (navigation bar)">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Website name</Label>
              <Input
                value={brand.site_name}
                onChange={(e) => setBrand((b) => ({ ...b, site_name: e.target.value }))}
                data-testid="settings-site-name"
                placeholder="Cheltenham Township"
              />
            </div>
            <div className="space-y-2">
              <Label>Tagline</Label>
              <Input
                value={brand.site_tagline}
                onChange={(e) => setBrand((b) => ({ ...b, site_tagline: e.target.value }))}
                data-testid="settings-site-tagline"
                placeholder="Tax Filing Portal"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              onClick={saveBrand}
              disabled={savingBrand}
              className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white"
              data-testid="save-brand-btn"
            >
              {savingBrand ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save site name
            </Button>
          </div>
        </Block>

        <section className="bg-white border border-slate-200 rounded-md p-6" data-testid="seo-block">
          <h3 className="font-heading text-lg font-medium text-slate-900 mb-1 flex items-center gap-2">
            <Search className="w-5 h-5 text-[#1E3A8A]" /> SEO &amp; Browser Tab
          </h3>
          <p className="text-sm text-slate-500 mb-4">
            Control the browser tab name and how the site appears in search results.
          </p>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Browser tab name (page title)</Label>
              <Input
                value={seo.page_title}
                onChange={(e) => setSeo((s) => ({ ...s, page_title: e.target.value }))}
                data-testid="settings-seo-title"
                placeholder="Cheltenham Township | Tax Filing Portal"
              />
            </div>
            <div className="space-y-2">
              <Label>Meta description</Label>
              <Textarea
                value={seo.meta_description}
                onChange={(e) => setSeo((s) => ({ ...s, meta_description: e.target.value }))}
                data-testid="settings-seo-description"
                rows={3}
                placeholder="A short summary shown in search engine results."
              />
            </div>
            <div className="space-y-2">
              <Label>Meta keywords (comma separated)</Label>
              <Input
                value={seo.meta_keywords}
                onChange={(e) => setSeo((s) => ({ ...s, meta_keywords: e.target.value }))}
                data-testid="settings-seo-keywords"
                placeholder="mercantile tax, business privilege tax, Montgomery County"
              />
            </div>
            <div className="flex justify-end">
              <Button
                onClick={saveSeo}
                disabled={savingSeo}
                className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white"
                data-testid="save-seo-btn"
              >
                {savingSeo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save SEO settings
              </Button>
            </div>
          </div>
        </section>

        <Block title="Hero Section">
          <div className="space-y-2">
            <Label>Badge text</Label>
            <Input value={data.badge} onChange={(e) => set("badge", e.target.value)} data-testid="settings-badge" />
          </div>
          <div className="space-y-2">
            <Label>Headline</Label>
            <Textarea value={data.title} onChange={(e) => set("title", e.target.value)} data-testid="settings-title" rows={2} />
          </div>
          <div className="space-y-2">
            <Label>Subtitle</Label>
            <Textarea value={data.subtitle} onChange={(e) => set("subtitle", e.target.value)} data-testid="settings-subtitle" rows={3} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Primary button label</Label>
              <Input value={data.cta_primary} onChange={(e) => set("cta_primary", e.target.value)} data-testid="settings-cta-primary" />
            </div>
            <div className="space-y-2">
              <Label>Secondary button label</Label>
              <Input value={data.cta_secondary} onChange={(e) => set("cta_secondary", e.target.value)} data-testid="settings-cta-secondary" />
            </div>
          </div>
        </Block>

        <Block title="Feature Cards">
          {data.features.map((f, i) => (
            <div key={i} className="border border-slate-200 rounded-md p-4 space-y-3">
              <p className="text-xs uppercase tracking-wide text-slate-400">Card {i + 1}</p>
              <div className="space-y-2">
                <Label>Title</Label>
                <Input value={f.title} onChange={(e) => setFeature(i, "title", e.target.value)} data-testid={`settings-feature-title-${i}`} />
              </div>
              <div className="space-y-2">
                <Label>Body</Label>
                <Textarea value={f.body} onChange={(e) => setFeature(i, "body", e.target.value)} data-testid={`settings-feature-body-${i}`} rows={2} />
              </div>
            </div>
          ))}
        </Block>

        <Block title="Footer">
          <div className="space-y-2">
            <Label>Footer note</Label>
            <Textarea value={data.footer} onChange={(e) => set("footer", e.target.value)} data-testid="settings-footer" rows={3} />
          </div>
        </Block>
      </div>
    </div>
  );
}
